import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface EmailLog {
  id: string;
  email_type: string;
  customer_email: string;
  customer_name: string;
  subject: string;
  status: string;
  sent_at: string;
  establishment_id: string;
  establishment_name?: string;
  error_message?: string;
}

export interface EmailStats {
  summary: {
    total: number;
    byType: Record<string, { sent: number; failed: number }>;
    byStatus: { sent: number; failed: number };
    byPeriod: { today: number; week: number; month: number };
  };
  timeline: Array<{
    date: string;
    [key: string]: number | string;
  }>;
  logs: EmailLog[];
  totalLogs: number;
  establishments: Array<{ id: string; name: string }>;
}

export interface AdminEmailStatsParams {
  period: "7d" | "30d" | "90d" | "all";
  emailType?: string;
  status?: string;
  establishmentId?: string;
  page?: number;
  limit?: number;
}

export const useAdminEmailStats = (params: AdminEmailStatsParams) => {
  return useQuery({
    queryKey: ["adminEmailStats", params],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Non authentifié");

      const queryParams = new URLSearchParams();
      queryParams.set("period", params.period);
      if (params.emailType) queryParams.set("emailType", params.emailType);
      if (params.status) queryParams.set("status", params.status);
      if (params.establishmentId) queryParams.set("establishmentId", params.establishmentId);
      if (params.page) queryParams.set("page", params.page.toString());
      if (params.limit) queryParams.set("limit", params.limit.toString());

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-admin-email-stats?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Erreur lors de la récupération des statistiques");
      }

      return (await response.json()) as EmailStats;
    },
    refetchInterval: 60000, // Refresh every minute
  });
};
